import React, { useState, useRef } from 'react';
import { useDispatch } from 'react-redux';

import LayoutWithBg from '../../components/layout/LayoutWithBg';
import {
  filterSubCharges,
  findVariantCharge,
  isChargeForThatProductVariant,
  needChargeForProduct,
  mapSaSwapProducts,
} from './utils';
import { REQUEST_DEVICE_SN } from './ActivationWizard/screenTypes';
import ActivationWizard from './ActivationWizard';
import { delay } from '../../utils/timers';
import fetchRequest from '../../utils/fetchRequest';
import { getThirtyDaysAgo } from '../../utils/dates';
import { useUpdateAdressMutation } from '../../redux/addressesApi';
import {
  chargesApi,
  useProcessChargeMutation,
  useLazyGetChargeQuery,
  useGetListChargesQuery,
} from '../../redux/chargesApi';
import { useGetProductsQuery } from '../../redux/productsApi';
import useGetActivationSubs from '../../hooks/useGetActivationSubs';
import { useGetListSubscriptionsQuery } from '../../redux/subscriptionsApi';

const DeviceActivation = () => {
  const [deviceSn, setDeviceSn] = useState('');
  const [device, setDevice] = useState(null);
  const [step, setStep] = useState(REQUEST_DEVICE_SN);
  const [sub, setSub] = useState(null);

  const alreadyActivatedSubsRef = useRef([]);
  const failedChargeRef = useRef();
  const restoredSelectedVariantRef = useRef(null);
  const restoredPaymentMethodIdRef = useRef(null);
  const dispatch = useDispatch();

  const { data: subscriptionsResp } = useGetListSubscriptionsQuery({
    status: 'cancelled',
  });
  const cancelledSubs = subscriptionsResp?.subscriptions;
  const [subsForActivation, subsLoading] = useGetActivationSubs();
  const notActivatedSubs = subsForActivation.filter(
    (sub) => !alreadyActivatedSubsRef.current.includes(sub.id),
  );
  const subsWithPlaceholderProducts =
    notActivatedSubs.filter(needChargeForProduct);
  const subsProductsIds = subsWithPlaceholderProducts.map((sub) =>
    mapSaSwapProducts(sub?.external_product_id?.ecommerce),
  );
  const { data: productsResp } = useGetProductsQuery(
    {
      external_product_ids: subsProductsIds,
      limit: 25,
      format_version: '2022-06',
    },
    { skip: !subsProductsIds.length },
  );
  const products = productsResp?.products || [];

  const { data: subsChargesResp } = useGetListChargesQuery({
    created_at_min: getThirtyDaysAgo(),
    limit: 250,
  });
  const subsCharges = subsChargesResp?.charges || [];

  const [processCharge] = useProcessChargeMutation();
  const [updateAddress] = useUpdateAdressMutation();
  const [fetchCharge] = useLazyGetChargeQuery();

  const changePlan = async (subId, variantId) => {
    const defaultErrorMessage = 'Failed to set new plan';

    try {
      const resp = await fetchRequest.post('/subscriptions/update_product', {
        recharge_sub_id: subId,
        recharge_new_variant_id: variantId,
      });
      return resp;
    } catch (error) {
      throw new Error(defaultErrorMessage);
    }
  };

  const fetchUpcomingCharge = async (subId, include) => {
    const defaultErrorMessage = 'Failed to find a charge';

    try {
      const params = {
        purchase_item_id: subId,
        status: 'queued',
      };
      if (include) {
        params.include = include;
      }
      const chargesResponse = await dispatch(
        chargesApi.endpoints.getListCharges.initiate(params),
        { track: false },
      );

      if (chargesResponse.error) {
        throw new Error(defaultErrorMessage);
      }

      const charge = chargesResponse.data.charges[0];
      return charge;
    } catch (error) {
      throw new Error(defaultErrorMessage);
    }
  };

  const handleProcessCharge = async (chargeId) => {
    const defaultErrorMessage = 'Failed to process payment';
    try {
      const resp = await processCharge({
        id: chargeId,
      });
      if (resp.error) {
        throw new Error(defaultErrorMessage);
      }
    } catch (error) {
      throw new Error(defaultErrorMessage);
    }
  };

  const handleSetPaymentMethod = async (addressId, paymentMethodId) => {
    const defaultErrorMessage = 'Failed to set payment method';
    try {
      const resp = await updateAddress({
        id: addressId,
        payment_method_id: paymentMethodId,
      });
      if (resp.error) {
        throw new Error(defaultErrorMessage);
      }
    } catch (error) {
      throw new Error(defaultErrorMessage);
    }
  };

  const registerActivation = async (chargeId) => {
    try {
      const response = await fetchRequest.post(
        `/subscriptions/${sub.id}/register_activation`,
        {
          chargeId,
          deviceSn,
          properties: sub.properties || [],
        },
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const waitForTheActivation = async ({
    chargeId,
    stage = 'payment',
    openProcessingScreen,
    openCompleteScreen,
  }) => {
    let response;
    openProcessingScreen(stage);
    try {
      const MAX_RETRIES = 120;
      let waitTime = 5000;
      let count = 0;

      await delay(waitTime);
      while (count < MAX_RETRIES) {
        response = await fetchRequest.get(
          `/subscriptions/${sub.id}/activation_status`,
          {
            chargeId,
          },
        );

        const stage = response.data?.stage;
        const status = response.data?.status;
        if (stage === 'activation') {
          openProcessingScreen('activation');
        }

        if (status === 'complete') {
          openCompleteScreen();
          return { success: true };
        } else if (status === 'error') {
          return { success: false, stage: stage };
        } else {
          count++;
          await delay(waitTime);
        }
      }

      if (count >= MAX_RETRIES) {
        return { success: false, stage: response?.data?.stage };
      }
    } catch (error) {
      return { success: false, stage: response?.data?.stage };
    }
  };

  const continueActivationAfterLostConnection = async ({
    setLoading,
    setError,
    openErrorScreen,
    openPaymentErrorScreen,
    openCompleteScreen,
    openProcessingScreen,
    openOfflineScreen,
  }) => {
    const paymentMethodId = restoredPaymentMethodIdRef.current;
    const productVariant = restoredSelectedVariantRef.current;
    if (!failedChargeRef.current) {
      activateNewLine({
        paymentMethodId,
        productVariant,
        setLoading,
        setError,
        openErrorScreen,
        openOfflineScreen,
        openPaymentErrorScreen,
        openCompleteScreen,
        openProcessingScreen,
      });

      return;
    }

    let charge;
    let activationResult;
    try {
      openProcessingScreen('payment');

      const { data } = await fetchCharge(
        {
          id: failedChargeRef.current.id,
        },
        false,
      );
      charge = data;
      if (!charge.id && charge.error) {
        throw new Error('Failed to load charge');
      }
      if (charge.status === 'error') {
        openPaymentErrorScreen(paymentMethodId);
        return;
      }

      if (charge.status === 'queued') {
        await registerActivation(charge.id);
        await handleProcessCharge(charge.id);
      }

      // wait for the purchase and activation processing
      activationResult = await waitForTheActivation({
        chargeId: charge.id,
        openProcessingScreen,
        openCompleteScreen,
      });

      if (!activationResult.success) throw new Error('Activation error');
      alreadyActivatedSubsRef.current.push(sub.id);

      restoredSelectedVariantRef.current = null;
      restoredPaymentMethodIdRef.current = null;
    } catch (error) {
      if (charge) {
        failedChargeRef.current = charge;
      }
      restoredSelectedVariantRef.current = productVariant;
      restoredPaymentMethodIdRef.current = paymentMethodId;
      if (!navigator.onLine) {
        openOfflineScreen();
        return;
      }
      if (activationResult?.stage === 'activation') {
        openErrorScreen();
      } else {
        setError(error.message);
        openPaymentErrorScreen(paymentMethodId);
      }
      return;
    }
  };

  const activateNewLine = async ({
    paymentMethodId,
    productVariant,
    setError,
    openErrorScreen,
    openOfflineScreen,
    openPaymentErrorScreen,
    openCompleteScreen,
    openProcessingScreen,
  }) => {
    openProcessingScreen('payment');

    let charge;
    let activationResult;
    try {
      // set payment method before payment
      // always called, because after updating payment method shopify can remove it from address
      await handleSetPaymentMethod(sub.address_id, paymentMethodId);
      // set plan
      await changePlan(sub.id, productVariant.external_variant_id);

      const subCharges = filterSubCharges(subsCharges, sub.id);
      const failedPreviousCharge = findVariantCharge(
        subCharges,
        sub.id,
        productVariant.external_variant_id,
        'error',
      );
      const errorCharge = failedChargeRef.current || failedPreviousCharge;
      if (
        errorCharge &&
        isChargeForThatProductVariant(
          errorCharge,
          productVariant.external_variant_id,
        )
      ) {
        // use previous error charge
        charge = errorCharge;
      } else {
        // get upcoming charge
        charge = await fetchUpcomingCharge(sub.id);
      }

      // register charge as selfactivation
      await registerActivation(charge.id);

      // init charge
      await handleProcessCharge(charge.id);

      // wait for the purchase and activation processing
      activationResult = await waitForTheActivation({
        chargeId: charge.id,
        openProcessingScreen,
        openCompleteScreen,
      });
      if (!activationResult.success) throw new Error('Activation error');
      alreadyActivatedSubsRef.current.push(sub.id);
    } catch (error) {
      if (charge) {
        failedChargeRef.current = charge;
      }
      restoredSelectedVariantRef.current = productVariant;
      if (!navigator.onLine) {
        restoredPaymentMethodIdRef.current = paymentMethodId;
        openOfflineScreen();
        return;
      }

      if (activationResult?.stage === 'activation') {
        openErrorScreen();
      } else {
        setError(error.message);
        openPaymentErrorScreen(paymentMethodId);
      }
      return;
    }

    restoredSelectedVariantRef.current = null;
    restoredPaymentMethodIdRef.current = null;
  };

  const activateSubscription = async ({
    variant,
    setError,
    openErrorScreen,
    openCompleteScreen,
    openProcessingScreen,
  }) => {
    if (variant?.data === 'Parking') {
      openErrorScreen(false);
      return;
    }

    if (setError) {
      setError('');
    } else {
      setError = openErrorScreen;
    }
    openProcessingScreen('activation');

    try {
      const upcomingCharge = await fetchUpcomingCharge(sub.id, 'customer');

      await fetchRequest.post(`/subscriptions/${sub.id}/activate`, {
        charge: upcomingCharge,
        deviceSn,
        properties: sub.properties || [],
      });

      const activationResult = await waitForTheActivation({
        chargeId: upcomingCharge.id,
        stage: 'activation',
        openProcessingScreen,
        openCompleteScreen,
      });

      if (!activationResult.success) {
        openErrorScreen();
        return;
      }
      alreadyActivatedSubsRef.current.push(sub.id);
    } catch (error) {
      openErrorScreen();
    }
  };

  const wizardProps = {
    step,
    device,
    deviceSn,
    setDevice,
    setDeviceSn,
    setStep,
    setSub,
    cancelledSubs,
    notActivatedSubs,
    subsLoading,
    sub,
    products,
    subsCharges,
    activateNewLine,
    activateSubscription,
    continueActivationAfterLostConnection,
    restoredSelectedVariant: restoredSelectedVariantRef.current,
  };

  return (
    <LayoutWithBg showLogo={step === REQUEST_DEVICE_SN}>
      <ActivationWizard {...wizardProps} />
    </LayoutWithBg>
  );
};

export default DeviceActivation;
